/**
 * Choosing what to request from the service catalog.
 *
 * A searchable list of `Card`s rather than a dropdown: the offerings carry a
 * description, a lead time and an approval flag, and a non-technical employee
 * picking "New laptop" needs to see those before committing, not after.
 *
 * The catalog is filtered server-side (`q`, `category_id`) so the known 25-item
 * cap in Ivanti's Template API cannot silently hide an offering the user
 * searched for — and when the server reports `truncated_upstream`, the list
 * says so instead of pretending to be complete.
 */

import { Icon, Search } from "@norican/icons";
import { Badge, Button, Card, Input, Typography } from "@norican/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  isRequesterUnresolvedResult,
  LoadingRows,
  ResultStateNotice,
} from "@/components/common";
import { useLocalizedText } from "@/features/assistant";
import { useT } from "@/i18n";

import type { RequestOfferingSummary } from "../api/contract";
import { useRequestOfferings, useTranslateTo } from "../api/queries";
import { LookupSelect } from "./lookup-select";
import { TranslatableTextView } from "./translatable-text";

/** How long the search box waits after the last keystroke before querying. */
const SEARCH_DEBOUNCE_MS = 350;

/** One page of offerings: Ivanti's Template API caps a single read at 25. */
const OFFERING_PAGE_SIZE = 25;

export interface OfferingPickerProps {
  onSelect: (offering: RequestOfferingSummary) => void;
}

export function OfferingPicker({ onSelect }: OfferingPickerProps) {
  const { t } = useT("tickets");
  const navigate = useNavigate();
  const translateTo = useTranslateTo();

  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (searchDraft === search) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSearch(searchDraft), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, search]);

  const offerings = useRequestOfferings({
    ...(search.length > 0 ? { q: search } : {}),
    ...(categoryId === null ? {} : { category_id: categoryId }),
    limit: OFFERING_PAGE_SIZE,
    offset: 0,
    translate_to: translateTo,
  });

  const items = offerings.data?.items ?? [];
  // Either the upstream read was capped or there is a further page we do not
  // fetch: the list is not the whole catalogue, so say so and point at search.
  const truncated =
    offerings.data?.truncated_upstream === true || offerings.data?.page.has_more === true;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          placeholder={t("create.offering.searchPlaceholder")}
          aria-label={t("create.offering.search")}
          startSlot={<Icon icon={Search} size="sm" />}
          className="min-w-60 flex-1"
        />
        <LookupSelect
          items={offerings.data?.categories ?? []}
          value={categoryId}
          onValueChange={setCategoryId}
          placeholder={t("create.offering.anyCategory")}
          clearLabel={t("create.offering.anyCategory")}
          aria-label={t("create.offering.category")}
        />
      </div>

      <ResultStateNotice result={offerings.data?.result} />

      {offerings.isPending ? (
        <LoadingRows rows={4} />
      ) : offerings.isError ? (
        <ErrorState error={offerings.error} onRetry={() => void offerings.refetch()} />
      ) : items.length === 0 ? (
        // The notice above explains an empty catalogue for an unknown requester.
        isRequesterUnresolvedResult(offerings.data?.result) ? null : (
          <EmptyStateBlock
            size="sm"
            title={t("create.offering.emptyTitle")}
            description={t("create.offering.emptyDescription")}
            actions={
              <Button
                size="sm"
                variant="outline"
                onClick={() => void navigate("/tickets/new?kind=INCIDENT")}
              >
                {t("kind.INCIDENT")}
              </Button>
            }
          />
        )
      ) : (
        <div className="flex flex-col gap-3">
          {truncated ? (
            <Typography variant="caption" tone="muted">
              {t("create.offering.truncated")}
            </Typography>
          ) : null}
          <ul className="grid gap-3 sm:grid-cols-2">
            {items.map((offering) => (
              <li key={offering.offering_id}>
                <OfferingCard offering={offering} onSelect={onSelect} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface OfferingCardProps {
  offering: RequestOfferingSummary;
  onSelect: (offering: RequestOfferingSummary) => void;
}

function OfferingCard({ offering, onSelect }: OfferingCardProps) {
  const { t } = useT("tickets");
  const localize = useLocalizedText();

  return (
    <Card variant="outline" className="h-full">
      <Card.Body>
        <div className="flex h-full flex-col gap-3">
          <Typography as="h3" variant="heading-5">
            <TranslatableTextView value={offering.name} hideBadge />
          </Typography>

          {offering.short_description === null ? null : (
            <Typography variant="body-sm" tone="muted">
              <TranslatableTextView value={offering.short_description} hideBadge />
            </Typography>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {offering.requires_approval ? (
              <Badge tone="warning" variant="subtle" size="xs">
                {t("create.offering.approvalRequired")}
              </Badge>
            ) : null}
            {offering.estimated_delivery_days === null ? null : (
              <Badge tone="info" variant="subtle" size="xs">
                {t("create.offering.delivery", { count: offering.estimated_delivery_days })}
              </Badge>
            )}
          </div>

          {offering.is_available ? null : (
            <Typography variant="caption" tone="danger">
              {/* `unavailable_reason` is a LocalizedString: resolve it through
                  the catalogues like every other server-authored string, and
                  fall back to the generic line rather than to the server's
                  English reference text. */}
              {localize(offering.unavailable_reason) || t("create.offering.unavailable")}
            </Typography>
          )}

          <div className="mt-auto pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!offering.is_available}
              onClick={() => onSelect(offering)}
            >
              {t("create.offering.select")}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
